/* eslint-disable semi */
/* eslint-disable comma-dangle */
// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Fragment } from 'react'
import { Slide, toast } from 'react-toastify'

import baseurl from '../../../@core/auth/jwt/baseurl'
import emiter from '../../../utility/hooks/emiiter'

const showErrorMsg = (error) => {
  toast.error(
    <Fragment>
      <div className="toastify-header">
        <div className="title-wrapper">
          <h6 className="toast-title fw-bold">Error</h6>
        </div>
      </div>
      <div className="toastify-body">
        <span>{error}</span>
      </div>
    </Fragment>,
    {
      icon: false,
      transition: Slide,
      hideProgressBar: false,
      autoClose: 2000
    }
  )
}

const showSuccessMsg = (msg) => {
  toast.success(
    <Fragment>
      <div className="toastify-header">
        <div className="title-wrapper">
          <h6 className="toast-title fw-bold">Success</h6>
        </div>
      </div>
      <div className="toastify-body">
        <span>{msg}</span>
      </div>
    </Fragment>,
    {
      icon: false,
      transition: Slide,
      hideProgressBar: false,
      autoClose: 2000
    }
  )
}

export const getData = createAsyncThunk(
  'appNationallist/getData',
  async (params) => {
    const response = await baseurl.get('/api/app/nationallist', {
      params
    })
    return {
      params,
      data: response.data.items,
      total: response.data.totalCount
    }
  },
  {
    dispatchConditionRejection: true
  }
)

export const getById = createAsyncThunk(
  'appNationallist/getById',
  async (id) => {
    try {
      const response = await baseurl.get(`/api/app/nationallist/${id}`, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (response.status === 200) {
        return response.data
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return rejectWithValue(response.data)
    } catch (error) {
      return rejectWithValue(error)
    }
  },
  {
    dispatchConditionRejection: true
  }
)

export const addNationallist = createAsyncThunk(
  'appNationallist/addNationallist',
  async (nationallist, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.post('/api/app/nationallist', nationallist, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 200) {
        showSuccessMsg('Added Successfully')
        emiter.emit('close-nationallist-add-or-edit-popup')
        await dispatch(getData(getState().nationallist.params))
        await dispatch(getById(res.data.id))
        return nationallist
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const updateNationallist = createAsyncThunk(
  'appNationallist/updateNationallist',
  async (nationallist, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.put(`/api/app/nationallist/${nationallist.id}`, nationallist, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 200) {
        showSuccessMsg('Updated Successfully')
        await dispatch(getData(getState().nationallist.params))
        await dispatch(getById(nationallist.id))
        return nationallist
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deleteNationallist = createAsyncThunk(
  'appNationallist/deleteNationallist',
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.delete(`/api/app/nationallist/${id}`, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 204) {
        emiter.emit('close-nationallist-delete-popup')
        await dispatch(getData(getState().nationallist.params))
        return id
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const initNationallist = createAsyncThunk('appNationallist/initNationallist', (loading) => loading, {})
export const loadNationallistLookups = createAsyncThunk(
  'appNationallist/loadNationallistLookups',
  async () => {
    const categoriesResponse = await baseurl.get('/api/app/nationallist-category', {
      params: {
        MaxResultCount: 1000
      }
    })
    const unitsResponse = await baseurl.get('/api/app/unit', {
      params: {
        MaxResultCount: 1000
      }
    })

    const dosageFormsResponse = await baseurl.get('/api/app/dosage-form', {
      params: {
        MaxResultCount: 1000
      }
    })

    const categoriesTreeResponse = await baseurl.get('/api/app/nationallist-category/tree', {
      params: {}
    })

    return {
      categories: categoriesResponse.data?.items,
      units: unitsResponse.data?.items,
      dosageForms: dosageFormsResponse.data?.items,
      categoriesTree: categoriesTreeResponse.data ?? []
    }
  },
  {}
)

export const appNationallistSlice = createSlice({
  name: 'appNationallist',
  initialState: {
    data: [],
    total: 1,
    params: {},
    allData: [],
    selectedNationallist: null,
    loading: true,
    getByIdLoading: false,
    saveLoading: false
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getData.pending, (state) => {
        state.loading = true
      })
      .addCase(getData.rejected, (state) => {
        state.loading = false
      })
      .addCase(getData.fulfilled, (state, action) => {
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.total
        state.loading = false
      })

      .addCase(getById.pending, (state) => {
        state.getByIdLoading = true
        state.selectedNationallist = null
      })
      .addCase(getById.rejected, (state) => {
        state.getByIdLoading = false
      })
      .addCase(getById.fulfilled, (state, action) => {
        state.getByIdLoading = false
        state.selectedNationallist = action.payload
      })

      .addCase(addNationallist.pending, (state) => {
        state.saveLoading = true
      })
      .addCase(addNationallist.rejected, (state) => {
        state.saveLoading = false
      })
      .addCase(addNationallist.fulfilled, (state) => {
        state.saveLoading = false
      })

      .addCase(updateNationallist.pending, (state) => {
        state.saveLoading = true
      })
      .addCase(updateNationallist.rejected, (state) => {
        state.saveLoading = false
      })
      .addCase(updateNationallist.fulfilled, (state) => {
        state.saveLoading = false
      })

      .addCase(deleteNationallist.pending, (state) => {
        state.deleteLoading = true
      })
      .addCase(deleteNationallist.rejected, (state) => {
        state.deleteLoading = false
      })
      .addCase(deleteNationallist.fulfilled, (state) => {
        state.deleteLoading = false
      })

      .addCase(loadNationallistLookups.fulfilled, (state, action) => {
        state.categories = action.payload.categories
        state.categoriesTree = action.payload.categoriesTree
        state.units = action.payload.units
        state.dosageForms = action.payload.dosageForms
      })
      .addCase(initNationallist.fulfilled, (state) => {
        state.selectedNationallist = null
      })
  }
})

export default appNationallistSlice.reducer
